import React, { useEffect, useState } from 'react';
import './tournaments.css';
import { API_BASE_URL } from '../../config';

export default function NotifyModal({ open, onClose, tournament }) {
  // --- Hooks (immer unconditionally) ---
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);

  const token =
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('accessToken');

  const tournamentId = tournament?._id || tournament?.id || null;
  const hasMongoId =
    typeof tournamentId === 'string' && /^[0-9a-fA-F]{24}$/.test(tournamentId);
  const isLoggedIn = !!token;

  // Reset bei Öffnen ODER wenn das Turnier wechselt
  useEffect(() => {
    setError('');
    setOkMsg('');
    setIsAlreadyRegistered(false);
    
    if (!open) return;

    // Vorfüllen aus lokalem Profil (ohne zusätzlichen API-Call)
    try {
      const raw =
        localStorage.getItem('user') || localStorage.getItem('profile');
      if (raw) {
        const u = JSON.parse(raw);
        if (u?.email) setEmail(u.email);
      }
    } catch {}

    // Check if already registered when modal opens
    checkRegistrationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tournamentId]);

  async function checkRegistrationStatus() {
    if (!hasMongoId) return;

    try {
      const url = new URL(`${API_BASE_URL}/notify/status/${tournamentId}`);
      if (!isLoggedIn && email) {
        url.searchParams.append('email', email);
      }

      const headers = {};
      if (isLoggedIn) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(url.toString(), { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.isRegistered) {
          setIsAlreadyRegistered(true);
          setOkMsg("You're already registered for notifications on this tournament.");
        }
      }
    } catch (e) {
      console.warn('Could not check registration status:', e);
    }
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    setError('');
    setOkMsg('');
    setSubmitting(true);

    try {
      let res;
      let requestBody = {};

      // Eingeloggt + echte Turnier-ID → user-gebundene Notification
      if (isLoggedIn && hasMongoId) {
        requestBody = { tournamentId };
        res = await fetch(`${API_BASE_URL}/notify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });
      } else {
        // Gast ODER statische ID → anonyme Notification mit Email
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
          setError('Please enter a valid email address.');
          setSubmitting(false);
          return;
        }

        requestBody = {
          email,
          tournamentId: hasMongoId ? tournamentId : undefined,
          tournamentTitle: hasMongoId ? undefined : (tournament?.title || ''),
          game: tournament?.game || undefined,
        };

        res = await fetch(`${API_BASE_URL}/notify/anonymous`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });
      }

      const responseData = await res.json();

      // Handle different response statuses
      if (res.status === 409) {
        setError(responseData.msg || 'You are already registered for this tournament.');
        setIsAlreadyRegistered(true);
        setSubmitting(false);
        return;
      }

      if (!res.ok) {
        throw new Error(responseData.msg || 'Registration failed');
      }

      // Success
      setOkMsg(responseData.msg || "You're on the list! We'll notify you when this tournament goes live.");
      setIsAlreadyRegistered(true);

      // Optional: Close modal after success (with delay)
      // setTimeout(() => onClose(), 2000);
    } catch (err) {
      console.error('Notify registration error:', err);
      setError(err.message || 'Could not register your notification. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUnsubscribe() {
    if (!isLoggedIn || !hasMongoId) return;
    
    setSubmitting(true);
    setError('');
    setOkMsg('');

    try {
      const resp = await fetch(`${API_BASE_URL}/notify/tournament/${tournamentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      // Check if response is ok before parsing JSON
      if (!resp.ok) {
        // Try to parse error message if JSON, otherwise use status text
        let errorMsg = 'Failed to unsubscribe. Please try again.';
        try {
          const errorData = await resp.json();
          errorMsg = errorData.msg || errorData.message || errorMsg;
        } catch (e) {
          errorMsg = `Server error: ${resp.status} ${resp.statusText}`;
        }
        setError(errorMsg);
        return;
      }

      const data = await resp.json();
      if (data.success) {
        setOkMsg('Successfully unsubscribed from notifications!');
        setIsAlreadyRegistered(false);
      } else {
        setError(data.msg || 'Failed to unsubscribe. Please try again.');
      }
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  const showEmailField = !isLoggedIn || !hasMongoId;

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.title}>
          {isAlreadyRegistered ? '✅ Already Registered' : '🔔 Get Notified'}
        </h3>
        <p style={styles.text}>
          {isAlreadyRegistered 
            ? `You're already on the list for "${tournament?.title}".`
            : `We'll ping you when "${tournament?.title}" opens for registration.`
          }
        </p>

        {/* Show login status info */}
        {isLoggedIn && (
          <div style={styles.statusInfo}>
            <span style={styles.statusBadge}>✅ Logged in</span>
            <span style={styles.statusText}>
              Notifications will be sent to your account email
            </span>
          </div>
        )}

        {/* Email field - only show when needed */}
        {showEmailField && !isAlreadyRegistered && (
          <div style={{ marginBottom: 12 }}>
            <label style={styles.label}>
              Email Address {!isLoggedIn && <span style={styles.required}>*</span>}
            </label>
            <input
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              disabled={submitting}
              required={!isLoggedIn}
            />
          </div>
        )}

        {error && <div style={{ ...styles.note, color: '#ff6b6b' }}>{error}</div>}
        {okMsg && <div style={{ ...styles.note, color: '#6ce29b' }}>{okMsg}</div>}

        <div style={styles.row}>
          <button 
            type="button" 
            onClick={onClose} 
            style={{ ...styles.btn, ...styles.btnGhost }}
          >
            {isAlreadyRegistered ? 'Done' : 'Cancel'}
          </button>
          {isAlreadyRegistered && isLoggedIn && hasMongoId ? (
            <button 
              type="button" 
              onClick={handleUnsubscribe}
              disabled={submitting}
              style={{
                ...styles.btn,
                background: '#dc3545',
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? '🔄 Unsubscribing...' : '🔕 Unsubscribe'}
            </button>
          ) : !isAlreadyRegistered && (
            <button 
              type="button" 
              disabled={submitting || (!isLoggedIn && !email)} 
              onClick={handleSubmit} 
              style={{
                ...styles.btn,
                opacity: (submitting || (!isLoggedIn && !email)) ? 0.6 : 1,
                cursor: (submitting || (!isLoggedIn && !email)) ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? 'Registering...' : '🔔 Notify Me'}
            </button>
          )}
        </div>

        {/* Tournament info footer */}
        <div style={styles.tournamentInfo}>
          <div style={styles.infoRow}>
            <span>🎮 {tournament?.game || 'TBA'}</span>
            <span>🏆 {tournament?.format || 'TBA'}</span>
          </div>
          {tournament?.startAt && (
            <div style={styles.infoRow}>
              <span>📅 {new Date(tournament.startAt).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modal: {
    width: 'min(520px, 92vw)',
    background: 'linear-gradient(135deg, #141827, #1a1a2e)',
    color: '#fff',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
    padding: 24,
    fontFamily: '"Orbitron", monospace',
  },
  title: { 
    margin: '0 0 8px 0', 
    fontSize: 22,
    fontWeight: 700,
    background: 'linear-gradient(135deg, #ff8c00, #ff6347)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  text: { 
    margin: '0 0 16px 0', 
    color: 'rgba(255,255,255,0.8)',
    lineHeight: 1.5,
  },
  statusInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
    padding: 12,
    background: 'rgba(255, 140, 0, 0.1)',
    borderRadius: 8,
    border: '1px solid rgba(255, 140, 0, 0.2)',
  },
  statusBadge: {
    fontSize: 12,
    fontWeight: 600,
    color: '#ff8c00',
  },
  statusText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.7)',
  },
  label: { 
    display: 'block', 
    fontSize: 13, 
    fontWeight: 600,
    marginBottom: 6, 
    color: 'rgba(255,255,255,0.8)' 
  },
  required: {
    color: '#ff8c00',
    marginLeft: 4,
  },
  input: {
    width: '100%',
    background: '#0f1322',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 10,
    padding: '12px 14px',
    outline: 'none',
    fontSize: 14,
    transition: 'border-color 0.2s ease',
    fontFamily: '"Orbitron", monospace',
  },
  note: { 
    margin: '8px 0 12px', 
    fontSize: 14,
    fontWeight: 500,
    padding: 8,
    borderRadius: 6,
    background: 'rgba(255,255,255,0.05)',
  },
  row: { 
    display: 'flex', 
    gap: 12, 
    justifyContent: 'flex-end',
    marginTop: 20,
  },
  btn: {
    height: 44,
    padding: '0 20px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #ff8c00, #ff6347)',
    color: '#fff',
    fontWeight: 600,
    fontSize: 14,
    transition: 'all 0.2s ease',
    fontFamily: '"Orbitron", monospace',
  },
  btnGhost: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
  },
  tournamentInfo: {
    marginTop: 16,
    padding: 12,
    background: 'rgba(255,255,255,0.02)',
    borderRadius: 8,
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    color: 'rgba(255,255,255,0.6)',
    margin: '4px 0',
  },
};
